import React, { useState } from "react";
import { AI_CENTER_DISCLAIMER, AI_CENTER_MODULES, aiRiskTone, prettyAiLabel } from "../../utils/aiCenterUtils";

const moduleStyle = {
  border: "1px solid rgba(15,111,255,0.14)",
  background: "#ffffff",
  borderRadius: 16,
  padding: 16,
  display: "grid",
  gap: 12,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
};

const copyStyle = { margin: 0, color: "#475569", fontSize: 13, lineHeight: 1.5 };
const tinyStyle = { margin: 0, color: "#64748b", fontSize: 11, lineHeight: 1.5 };
const titleStyle = { margin: 0, color: "#0f172a", fontSize: 16, fontWeight: 900, lineHeight: 1.25 };
const rowStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 };

function Badge({ children, tone = "ai" }) {
  const palette = tone === "danger"
    ? { background: "#fee2e2", color: "#991b1b", border: "#fecaca" }
    : tone === "warning"
    ? { background: "#fef3c7", color: "#92400e", border: "#fde68a" }
    : tone === "success"
    ? { background: "#dcfce7", color: "#166534", border: "#bbf7d0" }
    : { background: "#eff6ff", color: "#0f6fff", border: "#bfdbfe" };
  return (
    <span style={{ ...palette, border: `1px solid ${palette.border}`, padding: "5px 9px", borderRadius: 999, fontSize: 11, fontWeight: 900 }}>
      {children}
    </span>
  );
}

function ModuleHeader({ title, status = "Live", right = null }) {
  return (
    <div style={rowStyle}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Badge>AI</Badge>
        <h3 style={titleStyle}>{title}</h3>
        <Badge tone={status === "Coming Soon" ? "warning" : "success"}>{status}</Badge>
      </div>
      {right}
    </div>
  );
}

function EmptyProperty({ onFindProperty }) {
  return (
    <div style={{ ...moduleStyle, gridColumn: "1 / -1", background: "linear-gradient(135deg, #eff6ff, #ecfdf5)" }}>
      <ModuleHeader title="Choose a property to power property intelligence" />
      <p style={copyStyle}>Open a property from Home to generate risk, scam, neighborhood, timeline, move, and rent insights here.</p>
      <button type="button" style={buttonPrimary} onClick={onFindProperty}>Find a Property</button>
    </div>
  );
}

const buttonPrimary = {
  border: 0,
  borderRadius: 12,
  minHeight: 44,
  padding: "11px 14px",
  background: "linear-gradient(135deg, #0f6fff, #10b981)",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  width: "100%",
  boxSizing: "border-box",
};
const buttonSecondary = {
  ...buttonPrimary,
  color: "#0f6fff",
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
};
const inputStyle = {
  width: "100%",
  minHeight: 44,
  border: "1px solid #dbe4f0",
  borderRadius: 12,
  padding: "11px 12px",
  font: "inherit",
  fontSize: 14,
  boxSizing: "border-box",
  color: "#0f172a",
  background: "#fff",
};

export default function AICenter({
  selectedProperty,
  propertyIssues = [],
  isPhoneMobile,
  onFindProperty,
  onAnalyzeRisk,
  riskLoading,
  riskError,
  onMakeReviewSafer,
  onScreenPropertyScam,
  onScreenTextScam,
  onRunAIFeature,
  scamLoading,
  scamError,
  onAnalyzeMaintenanceIssue,
  maintenanceLoadingId,
  leaseText,
  setLeaseText,
  leaseExplanation,
  leaseLoading,
  leaseError,
  onExplainLease,
  onAnalyzeNeighborhood,
  neighborhoodLoading,
  neighborhoodError,
  onAnalyzeReputation,
  reputationLoading,
  moveChecklistMode,
  setMoveChecklistMode,
  moveChecklist,
  moveChecklistLoading,
  moveChecklistError,
  onCreateMoveChecklist,
  onToggleMoveItem,
  smartMatchPreferences,
  setSmartMatchPreferences,
  smartMatches,
  smartMatchLoading,
  smartMatchError,
  smartMatchDisclaimer,
  onFindMatches,
  onOpenMatchedProperty,
  formatMonthlyRent,
  onEstimateRent,
  rentLoading,
  rentError,
}) {
  const [reviewDraft, setReviewDraft] = useState("");
  const [saferReview, setSaferReview] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [scamText, setScamText] = useState("");
  const [textScamResult, setTextScamResult] = useState(null);
  const [textScamLoading, setTextScamLoading] = useState(false);
  const [textScamError, setTextScamError] = useState("");
  const [leaseFiles, setLeaseFiles] = useState([]);
  const [leaseAnalyzerResult, setLeaseAnalyzerResult] = useState(null);
  const [leaseAnalyzerLoading, setLeaseAnalyzerLoading] = useState(false);
  const [leaseAnalyzerError, setLeaseAnalyzerError] = useState("");
  const [scamFiles, setScamFiles] = useState([]);
  const [scamPackageResult, setScamPackageResult] = useState(null);
  const [inspectionFiles, setInspectionFiles] = useState([]);
  const [inspectionNotes, setInspectionNotes] = useState("");
  const [inspectionResult, setInspectionResult] = useState(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState("");
  const [landlordReviewText, setLandlordReviewText] = useState("");
  const [landlordResponseResult, setLandlordResponseResult] = useState(null);
  const [landlordResponseLoading, setLandlordResponseLoading] = useState(false);
  const [landlordResponseError, setLandlordResponseError] = useState("");
  const [pmCopilotText, setPmCopilotText] = useState("");
  const [pmCopilotResult, setPmCopilotResult] = useState(null);
  const [pmCopilotLoading, setPmCopilotLoading] = useState(false);
  const [pmCopilotError, setPmCopilotError] = useState("");
  const [marketScope, setMarketScope] = useState("");
  const [marketInsightsResult, setMarketInsightsResult] = useState(null);
  const [marketInsightsLoading, setMarketInsightsLoading] = useState(false);
  const [marketInsightsError, setMarketInsightsError] = useState("");
  const [disputeFiles, setDisputeFiles] = useState([]);
  const [disputeText, setDisputeText] = useState("");
  const [disputeResult, setDisputeResult] = useState(null);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputeError, setDisputeError] = useState("");
  const propertyName = selectedProperty?.title || selectedProperty?.address || "Selected property";

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.length ? (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {value.map((item, index) => <li key={`${String(item).slice(0, 20)}-${index}`}>{typeof item === "object" ? JSON.stringify(item) : String(item)}</li>)}
        </ul>
      ) : null;
    }
    if (value && typeof value === "object") {
      return <p style={copyStyle}>{JSON.stringify(value)}</p>;
    }
    return <p style={copyStyle}>{String(value || "")}</p>;
  };

  const ResultPanel = ({ result, title = "AI result" }) => result ? (
    <div style={{ padding: 12, borderRadius: 12, background: "#f8fafc", display: "grid", gap: 8 }}>
      <div style={rowStyle}>
        <strong>{title}</strong>
        <button type="button" style={{ ...buttonSecondary, width: "auto", minHeight: 36, padding: "8px 10px" }} onClick={() => downloadPdfReport(title, result)}>
          Download PDF report
        </button>
      </div>
      {Object.entries(result)
        .filter(([key]) => !["model", "feature", "title"].includes(key))
        .map(([key, value]) => value ? (
          <div key={key}>
            <strong style={{ color: "#334155", fontSize: 12 }}>{prettyAiLabel(key)}</strong>
            {renderValue(value)}
          </div>
        ) : null)}
    </div>
  ) : null;

  const downloadPdfReport = (title, result) => {
    const lines = [
      title,
      "RentDuo AI Report",
      "",
      ...Object.entries(result || {}).flatMap(([key, value]) => [
        prettyAiLabel(key),
        Array.isArray(value) ? value.join("; ") : typeof value === "object" ? JSON.stringify(value) : String(value || ""),
        "",
      ]),
    ].join("\n").match(/.{1,86}/g) || [];
    const escapePdf = (text) => String(text).replace(/[\\()]/g, "\\$&");
    const body = lines.slice(0, 48).map((line, index) => `BT /F1 10 Tf 48 ${760 - index * 14} Td (${escapePdf(line)}) Tj ET`).join("\n");
    const objects = [
      "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
      "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
      "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj",
      "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
      `5 0 obj << /Length ${body.length} >> stream\n${body}\nendstream endobj`,
    ];
    let pdf = "%PDF-1.4\n";
    const offsets = [0];
    objects.forEach((object) => {
      offsets.push(pdf.length);
      pdf += `${object}\n`;
    });
    const xrefAt = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    offsets.slice(1).forEach((offset) => {
      pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
    });
    pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
    const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-rentduo-ai-report.pdf`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const runFeature = async ({ feature, payload, files, setResult, setLoading, setError }) => {
    if (!onRunAIFeature) return setError("RentDuo AI feature runner is unavailable.");
    try {
      setLoading(true);
      setError("");
      const result = await onRunAIFeature({ feature, payload, files });
      setResult(result);
    } catch (error) {
      setError(error?.message || "RentDuo AI could not complete that analysis right now.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaferReview = async () => {
    if (!reviewDraft.trim()) return setReviewError("Enter review text first.");
    try {
      setReviewLoading(true);
      setReviewError("");
      setSaferReview(await onMakeReviewSafer(reviewDraft));
    } catch (error) {
      setReviewError(error?.message || "Review rewrite is unavailable right now.");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleTextScamScreen = async () => {
    if (!scamText.trim()) return setTextScamError("Paste listing or message text first.");
    try {
      setTextScamLoading(true);
      setTextScamError("");
      setTextScamResult(await onScreenTextScam(scamText));
    } catch (error) {
      setTextScamError(error?.message || "Scam screening is unavailable right now.");
    } finally {
      setTextScamLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <section style={{
        borderRadius: 22,
        padding: isPhoneMobile ? 18 : 24,
        background: "linear-gradient(135deg, #eaf3ff 0%, #e9fbf3 100%)",
        border: "1px solid rgba(15,111,255,0.16)",
        display: "grid",
        gap: 12,
      }}>
        <div style={rowStyle}>
          <Badge>Powered by RentDuo AI</Badge>
          <Badge tone="warning">AI Rental Advisor</Badge>
        </div>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: isPhoneMobile ? 25 : 30, fontWeight: 950 }}>AI Center</h2>
        <p style={{ ...copyStyle, fontSize: 14 }}>
          Rental trust intelligence, safer writing, practical document guidance, and property decision tools in one place.
        </p>
        <p style={{ ...tinyStyle, fontWeight: 800 }}>{AI_CENTER_DISCLAIMER}</p>
      </section>

      {!selectedProperty ? <EmptyProperty onFindProperty={onFindProperty} /> : (
        <div style={{ ...moduleStyle, borderColor: "#bbf7d0", background: "#f0fdf4" }}>
          <div style={rowStyle}>
            <div>
              <p style={{ ...tinyStyle, textTransform: "uppercase", fontWeight: 900, color: "#047857" }}>Analyzing Property</p>
              <h3 style={{ ...titleStyle, marginTop: 5 }}>{propertyName}</h3>
              <p style={copyStyle}>{[selectedProperty.city, selectedProperty.state].filter(Boolean).join(", ")}</p>
            </div>
            {selectedProperty.isForRent && selectedProperty.monthlyRent ? <Badge tone="success">{formatMonthlyRent(selectedProperty.monthlyRent)}</Badge> : null}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14, alignItems: "start" }}>
        <section style={moduleStyle}>
          <ModuleHeader
            title={AI_CENTER_MODULES[0].title}
            right={selectedProperty?.aiRiskIntelligence?.riskLevel ? <Badge tone={aiRiskTone(selectedProperty.aiRiskIntelligence.riskLevel)}>{selectedProperty.aiRiskIntelligence.riskLevel} Risk</Badge> : null}
          />
          {selectedProperty?.aiRiskIntelligence ? (
            <>
              <p style={copyStyle}>{selectedProperty.aiRiskIntelligence.summary}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 7 }}>
                {Object.entries(selectedProperty.aiRiskIntelligence.categories || {}).map(([key, score]) => (
                  <div key={key} style={{ padding: 9, borderRadius: 10, background: "#f8fafc", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 4 }}>
                    <span>{prettyAiLabel(key)}</span><strong>{Number(score || 0)}</strong>
                  </div>
                ))}
              </div>
              {selectedProperty.aiRiskIntelligence.commonSignals?.length ? <p style={copyStyle}>Warnings: {selectedProperty.aiRiskIntelligence.commonSignals.join(" | ")}</p> : null}
            </>
          ) : <p style={copyStyle}>Select a property and generate a score from available reviews and issue trends.</p>}
          {riskError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{riskError}</p> : null}
          <button type="button" style={buttonSecondary} onClick={onAnalyzeRisk} disabled={!selectedProperty || riskLoading}>
            {riskLoading ? "Analyzing..." : selectedProperty?.aiRiskIntelligence ? "Refresh Risk Score" : "Generate Risk Score"}
          </button>
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[1].title} />
          <textarea style={{ ...inputStyle, minHeight: 96 }} value={reviewDraft} onChange={(event) => setReviewDraft(event.target.value)} placeholder="Draft a truthful first-hand review..." />
          <button type="button" style={buttonSecondary} onClick={handleSaferReview} disabled={reviewLoading}>
            {reviewLoading ? "Making Safer..." : "Make Review Safer"}
          </button>
          {reviewError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{reviewError}</p> : null}
          {saferReview ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ padding: 10, borderRadius: 10, background: "#f8fafc" }}><strong>Before</strong><p style={copyStyle}>{reviewDraft}</p></div>
              <div style={{ padding: 10, borderRadius: 10, background: "#ecfdf5" }}><strong>Safer draft</strong><p style={copyStyle}>{saferReview}</p></div>
            </div>
          ) : null}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[2].title} right={selectedProperty?.aiScamIntelligence?.riskLevel ? <Badge tone={aiRiskTone(selectedProperty.aiScamIntelligence.riskLevel)}>{selectedProperty.aiScamIntelligence.riskLevel}</Badge> : null} />
          {selectedProperty?.aiScamIntelligence ? <p style={copyStyle}>{selectedProperty.aiScamIntelligence.explanation}</p> : null}
          <button type="button" style={buttonSecondary} onClick={onScreenPropertyScam} disabled={!selectedProperty || scamLoading}>
            {scamLoading ? "Screening Property..." : "Screen Selected Property"}
          </button>
          <textarea style={{ ...inputStyle, minHeight: 76 }} value={scamText} onChange={(event) => setScamText(event.target.value)} placeholder="Or paste a suspicious listing/message..." />
          <button type="button" style={buttonSecondary} onClick={handleTextScamScreen} disabled={textScamLoading}>
            {textScamLoading ? "Screening Text..." : "Screen Pasted Text"}
          </button>
          <input style={inputStyle} type="file" multiple accept="image/*,.txt,.pdf" onChange={(event) => setScamFiles(Array.from(event.target.files || []))} />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "scam_detector",
              payload: { text: scamText, propertyId: selectedProperty?.id || "", propertyName },
              files: scamFiles,
              setResult: setScamPackageResult,
              setLoading: setTextScamLoading,
              setError: setTextScamError,
            })}
            disabled={textScamLoading}
          >
            {textScamLoading ? "Analyzing..." : "Analyze Listing / Screenshots"}
          </button>
          {(textScamResult || selectedProperty?.aiScamIntelligence)?.safetyTips?.length ? (
            <p style={copyStyle}>Tips: {(textScamResult || selectedProperty.aiScamIntelligence).safetyTips.join(" | ")}</p>
          ) : null}
          {scamError || textScamError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{scamError || textScamError}</p> : null}
          <ResultPanel result={scamPackageResult} title="AI Scam Detector Report" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[3].title} />
          <p style={copyStyle}>Analyze property issues already submitted privately. Photo-based analysis is <strong>Coming Soon</strong>.</p>
          {propertyIssues.length ? propertyIssues.slice(0, 3).map((issue) => (
            <div key={issue.id} style={{ padding: 10, borderRadius: 10, background: "#f8fafc", display: "grid", gap: 7 }}>
              <div style={rowStyle}>
                <strong>{prettyAiLabel(issue.issueType || "Issue")}</strong>
                {issue.aiMaintenanceAnalysis?.severity ? <Badge tone={aiRiskTone(issue.aiMaintenanceAnalysis.severity)}>{issue.aiMaintenanceAnalysis.severity}</Badge> : null}
              </div>
              <p style={copyStyle}>{issue.aiMaintenanceAnalysis?.summaryForManager || issue.issueText || "Submitted maintenance issue"}</p>
              {!issue.aiMaintenanceAnalysis ? (
                <button type="button" style={buttonSecondary} onClick={() => onAnalyzeMaintenanceIssue(issue)} disabled={maintenanceLoadingId === issue.id}>
                  {maintenanceLoadingId === issue.id ? "Analyzing..." : "Analyze Issue"}
                </button>
              ) : null}
            </div>
          )) : <p style={copyStyle}>No private property issue is available to analyze for this selected property.</p>}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[4].title} />
          <textarea style={{ ...inputStyle, minHeight: 96 }} value={leaseText} onChange={(event) => setLeaseText(event.target.value)} placeholder="Paste lease language to explain..." />
          <button type="button" style={buttonSecondary} onClick={onExplainLease} disabled={leaseLoading}>
            {leaseLoading ? "Explaining..." : "Explain Lease"}
          </button>
          {leaseExplanation ? (
            <>
              <p style={copyStyle}>{leaseExplanation.plainSummary}</p>
              {leaseExplanation.clausesToReview?.length ? <p style={copyStyle}>Review: {leaseExplanation.clausesToReview.join(" | ")}</p> : null}
            </>
          ) : null}
          {leaseError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{leaseError}</p> : null}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[5].title} />
          {selectedProperty?.aiNeighborhoodIntelligence ? (
            <>
              <p style={copyStyle}>{selectedProperty.aiNeighborhoodIntelligence.summary}</p>
              {selectedProperty.aiNeighborhoodIntelligence.considerations?.length ? <p style={copyStyle}>Considerations: {selectedProperty.aiNeighborhoodIntelligence.considerations.join(" | ")}</p> : null}
            </>
          ) : <p style={copyStyle}>Area sentiment, parking, noise, transit, and renter-fit insights when data exists.</p>}
          {neighborhoodError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{neighborhoodError}</p> : null}
          <button type="button" style={buttonSecondary} onClick={onAnalyzeNeighborhood} disabled={!selectedProperty || neighborhoodLoading}>
            {neighborhoodLoading ? "Analyzing..." : "Generate Area Insight"}
          </button>
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[6].title} />
          {selectedProperty?.aiReputationTimeline ? (
            <>
              <Badge>{prettyAiLabel(selectedProperty.aiReputationTimeline.trend)}</Badge>
              <p style={copyStyle}>{selectedProperty.aiReputationTimeline.summary}</p>
            </>
          ) : <p style={copyStyle}>See whether recent property activity appears improving, stable, or declining.</p>}
          <button type="button" style={buttonSecondary} onClick={onAnalyzeReputation} disabled={!selectedProperty || reputationLoading}>
            {reputationLoading ? "Summarizing..." : "Generate Timeline Summary"}
          </button>
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[7].title} status="Early Access" />
          <input style={inputStyle} inputMode="numeric" value={smartMatchPreferences.budget} onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, budget: event.target.value }))} placeholder="Monthly budget" />
          <input style={inputStyle} value={smartMatchPreferences.preferredCity} onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, preferredCity: event.target.value }))} placeholder="Preferred city" />
          <button type="button" style={buttonSecondary} onClick={onFindMatches} disabled={smartMatchLoading}>
            {smartMatchLoading ? "Finding Matches..." : "Find Recommended Rentals"}
          </button>
          {smartMatches.map((match) => (
            <button key={match.id} type="button" style={{ ...buttonSecondary, textAlign: "left" }} onClick={() => onOpenMatchedProperty(match)}>
              {match.title} - {formatMonthlyRent(match.monthlyRent)} - {match.matchScore}% fit
            </button>
          ))}
          {smartMatchError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{smartMatchError}</p> : null}
          {smartMatchDisclaimer ? <p style={tinyStyle}>{smartMatchDisclaimer}</p> : null}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[8].title} />
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <button type="button" style={{ ...(moveChecklistMode === "move_in" ? buttonPrimary : buttonSecondary), flex: "1 1 0", width: "auto" }} onClick={() => setMoveChecklistMode("move_in")}>Move In</button>
            <button type="button" style={{ ...(moveChecklistMode === "move_out" ? buttonPrimary : buttonSecondary), flex: "1 1 0", width: "auto" }} onClick={() => setMoveChecklistMode("move_out")}>Move Out</button>
          </div>
          {moveChecklist?.items?.length ? moveChecklist.items.map((item) => (
            <label key={item.id} style={{ ...copyStyle, display: "flex", gap: 8 }}>
              <input type="checkbox" checked={Boolean(item.completed)} onChange={() => onToggleMoveItem(item.id)} />
              <span style={{ textDecoration: item.completed ? "line-through" : "none" }}>{item.label}</span>
            </label>
          )) : <p style={copyStyle}>Generate reminders for photos, damage records, utilities, keys, and walkthroughs.</p>}
          <button type="button" style={buttonSecondary} onClick={onCreateMoveChecklist} disabled={!selectedProperty || moveChecklistLoading}>
            {moveChecklistLoading ? "Creating..." : "Generate Checklist"}
          </button>
          {moveChecklistError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{moveChecklistError}</p> : null}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[9].title} status="Early Access" />
          {selectedProperty?.aiMarketRentEstimate ? (
            <>
              <Badge>{prettyAiLabel(selectedProperty.aiMarketRentEstimate.comparison)}</Badge>
              <p style={copyStyle}>{selectedProperty.aiMarketRentEstimate.summary}</p>
              {selectedProperty.aiMarketRentEstimate.medianComparableRent ? <p style={copyStyle}>Comparison median: {formatMonthlyRent(selectedProperty.aiMarketRentEstimate.medianComparableRent)}</p> : null}
            </>
          ) : <p style={copyStyle}>Compare listed rent against available RentDuo listings when sufficient data exists.</p>}
          <button type="button" style={buttonSecondary} onClick={onEstimateRent} disabled={!selectedProperty || rentLoading}>
            {rentLoading ? "Estimating..." : "Compare Rent"}
          </button>
          {rentError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{rentError}</p> : null}
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[10].title} />
          <p style={copyStyle}>Upload a lease PDF, DOCX, image, or paste lease language. Text files are extracted locally; PDFs/DOCX are saved privately and analyzed from metadata plus any pasted text.</p>
          <input style={inputStyle} type="file" multiple accept=".pdf,.doc,.docx,.txt,image/*" onChange={(event) => setLeaseFiles(Array.from(event.target.files || []))} />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "lease_analyzer",
              payload: { leaseText, propertyId: selectedProperty?.id || "", propertyName },
              files: leaseFiles,
              setResult: setLeaseAnalyzerResult,
              setLoading: setLeaseAnalyzerLoading,
              setError: setLeaseAnalyzerError,
            })}
            disabled={leaseAnalyzerLoading}
          >
            {leaseAnalyzerLoading ? "Analyzing Lease..." : "Analyze Lease"}
          </button>
          {leaseAnalyzerError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{leaseAnalyzerError}</p> : null}
          <ResultPanel result={leaseAnalyzerResult} title="AI Lease Analyzer" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[11].title} />
          <p style={copyStyle}>Upload move-in photos or short video evidence and add room notes to create a condition report.</p>
          <input style={inputStyle} type="file" multiple accept="image/*,video/*" onChange={(event) => setInspectionFiles(Array.from(event.target.files || []))} />
          <textarea style={{ ...inputStyle, minHeight: 76 }} value={inspectionNotes} onChange={(event) => setInspectionNotes(event.target.value)} placeholder="Rooms, dates, visible damage, or inspection notes..." />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "move_in_inspection",
              payload: { notes: inspectionNotes, propertyId: selectedProperty?.id || "", propertyName },
              files: inspectionFiles,
              setResult: setInspectionResult,
              setLoading: setInspectionLoading,
              setError: setInspectionError,
            })}
            disabled={inspectionLoading}
          >
            {inspectionLoading ? "Creating Report..." : "Generate Move-In Report"}
          </button>
          {inspectionError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{inspectionError}</p> : null}
          <ResultPanel result={inspectionResult} title="AI Move-In Inspection Report" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[12].title} />
          <textarea style={{ ...inputStyle, minHeight: 96 }} value={landlordReviewText} onChange={(event) => setLandlordReviewText(event.target.value)} placeholder="Paste the review or situation you want to respond to..." />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "landlord_response",
              payload: { reviewText: landlordReviewText, propertyName },
              files: [],
              setResult: setLandlordResponseResult,
              setLoading: setLandlordResponseLoading,
              setError: setLandlordResponseError,
            })}
            disabled={landlordResponseLoading}
          >
            {landlordResponseLoading ? "Drafting..." : "Draft Professional Response"}
          </button>
          {landlordResponseError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{landlordResponseError}</p> : null}
          <ResultPanel result={landlordResponseResult} title="AI Landlord Response" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[13].title} />
          <textarea style={{ ...inputStyle, minHeight: 96 }} value={pmCopilotText} onChange={(event) => setPmCopilotText(event.target.value)} placeholder="Paste maintenance requests, tenant message context, or portfolio notes..." />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "property_manager_copilot",
              payload: { notes: pmCopilotText, propertyName, openIssueCount: propertyIssues.length },
              files: [],
              setResult: setPmCopilotResult,
              setLoading: setPmCopilotLoading,
              setError: setPmCopilotError,
            })}
            disabled={pmCopilotLoading}
          >
            {pmCopilotLoading ? "Thinking..." : "Ask PM Copilot"}
          </button>
          {pmCopilotError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{pmCopilotError}</p> : null}
          <ResultPanel result={pmCopilotResult} title="AI Property Manager Copilot" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[14].title} status="Early Access" />
          <input style={inputStyle} value={marketScope} onChange={(event) => setMarketScope(event.target.value)} placeholder="City, county, or state, for example Newton, Sussex County, NJ" />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "market_insights",
              payload: { marketScope, selectedPropertyCity: selectedProperty?.city || "", selectedPropertyState: selectedProperty?.state || "" },
              files: [],
              setResult: setMarketInsightsResult,
              setLoading: setMarketInsightsLoading,
              setError: setMarketInsightsError,
            })}
            disabled={marketInsightsLoading}
          >
            {marketInsightsLoading ? "Summarizing..." : "Generate Market Summary"}
          </button>
          {marketInsightsError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{marketInsightsError}</p> : null}
          <ResultPanel result={marketInsightsResult} title="AI Rental Market Insights" />
        </section>

        <section style={moduleStyle}>
          <ModuleHeader title={AI_CENTER_MODULES[15].title} />
          <p style={copyStyle}>Upload move-out photos, deductions, or message screenshots and describe the timeline. RentDuo AI organizes evidence only; it does not give legal advice.</p>
          <input style={inputStyle} type="file" multiple accept=".pdf,.txt,image/*,video/*" onChange={(event) => setDisputeFiles(Array.from(event.target.files || []))} />
          <textarea style={{ ...inputStyle, minHeight: 96 }} value={disputeText} onChange={(event) => setDisputeText(event.target.value)} placeholder="Deposit deductions, timeline, messages, and missing documents..." />
          <button
            type="button"
            style={buttonSecondary}
            onClick={() => runFeature({
              feature: "move_out_dispute",
              payload: { disputeText, propertyName },
              files: disputeFiles,
              setResult: setDisputeResult,
              setLoading: setDisputeLoading,
              setError: setDisputeError,
            })}
            disabled={disputeLoading}
          >
            {disputeLoading ? "Preparing..." : "Generate Dispute Prep Report"}
          </button>
          {disputeError ? <p style={{ ...copyStyle, color: "#b91c1c" }}>{disputeError}</p> : null}
          <ResultPanel result={disputeResult} title="AI Move-Out Dispute Report" />
        </section>
      </div>

      <p style={{ ...tinyStyle, padding: "0 4px", fontWeight: 800 }}>{AI_CENTER_DISCLAIMER}</p>
    </div>
  );
}
